import React, { useState } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Dialog "Segna come Spedito": tracking + quantità per riga (evasione totale o parziale). */
export const ShipOrderDialog = ({ order, onDone, onCancel }) => {
  const items = order.items || [];
  const remainingOf = (it) => Number(it.quantity || 0) - Number(it.shipped_qty || 0);
  const [tracking, setTracking] = useState("");
  const [qty, setQty] = useState(() => items.map((it) => String(remainingOf(it))));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const parsed = qty.map((q) => parseInt(q, 10) || 0);
  const totalNow = parsed.reduce((s, q) => s + q, 0);
  const totalRemaining = items.reduce((s, it) => s + remainingOf(it), 0);
  const isPartial = totalNow < totalRemaining;
  const invalid = parsed.some((q, i) => q < 0 || q > remainingOf(items[i]));

  const setAll = (full) => setQty(items.map((it) => (full ? String(remainingOf(it)) : "0")));

  const submit = async () => {
    if (!tracking.trim()) { setError("Inserisci il numero di tracking"); return; }
    if (totalNow === 0) { setError("Seleziona almeno un pezzo da spedire"); return; }
    if (invalid) { setError("Quantità non valida"); return; }
    setBusy(true); setError("");
    try {
      const token = sessionStorage.getItem("access_token");
      const body = { tracking_number: tracking.trim(), items: parsed.map((q, i) => ({ index: i, quantity: q })).filter((r) => r.quantity > 0) };
      const res = await axios.post(`${API}/orders/${order.id}/ship`, body, { headers: { Authorization: `Bearer ${token}` } });
      onDone(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore durante la registrazione della spedizione");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg mb-4 space-y-3" data-testid={`ship-dialog-${order.id}`}>
      <h4 className="font-semibold text-gray-900">📦 {order.status === "partially_shipped" ? "Spedisci i pezzi rimanenti" : "Segna come Spedito"}</h4>
      <Input placeholder="Numero tracking…" value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid={`ship-tracking-input-${order.id}`} />
      <div className="text-xs text-gray-600 flex items-center gap-3">
        <span>Quanti pezzi spedisci ora per ogni articolo?</span>
        <button type="button" className="underline" onClick={() => setAll(true)} data-testid={`ship-all-${order.id}`}>tutti</button>
        <button type="button" className="underline" onClick={() => setAll(false)} data-testid={`ship-none-${order.id}`}>nessuno</button>
      </div>
      <div className="space-y-1.5">
        {items.map((it, i) => {
          const rem = remainingOf(it);
          return (
            <div key={i} className={`flex items-center justify-between gap-3 text-sm ${rem === 0 ? "opacity-50" : ""}`} data-testid={`ship-item-row-${order.id}-${i}`}>
              <span className="truncate">
                {it.title || it.article_id}
                {it.availability_type === "preorder" && <span className="ml-1 text-[10px] font-bold text-purple-700">PREVENDITA</span>}
                <span className="text-gray-500 ml-1">(da spedire: {rem})</span>
              </span>
              <input type="number" min={0} max={rem} inputMode="numeric" disabled={rem === 0} value={qty[i]}
                onChange={(e) => setQty(qty.map((q, j) => (j === i ? e.target.value : q)))}
                className="w-16 border rounded px-2 py-1 text-sm bg-white" data-testid={`ship-item-qty-${order.id}-${i}`} />
            </div>
          );
        })}
      </div>
      <p className={`text-xs font-semibold ${isPartial ? "text-orange-700" : "text-green-700"}`} data-testid={`ship-summary-${order.id}`}>
        {isPartial
          ? `⚠️ Spedizione PARZIALE: ${totalNow} di ${totalRemaining} pezzi. L'ordine resterà "Da gestire" fino alla spedizione del resto; il cliente riceverà l'email con i pezzi spediti e quelli in arrivo.`
          : `✅ Spedizione completa: ${totalNow} pezzi. L'ordine passerà a "Spedito".`}
      </p>
      {error && <p className="text-xs text-red-600 font-semibold" data-testid={`ship-error-${order.id}`}>{error}</p>}
      <div className="flex gap-2">
        <Button size="sm" onClick={submit} disabled={busy || !tracking.trim() || totalNow === 0 || invalid} className="bg-purple-600 hover:bg-purple-700" data-testid={`ship-confirm-${order.id}`}>
          {busy ? "Registro…" : isPartial ? "✅ Conferma spedizione parziale" : "✅ Conferma spedizione"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} data-testid={`ship-cancel-${order.id}`}>Annulla</Button>
      </div>
    </div>
  );
};
